import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import axios from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import { ScanManagerErrorColumns } from 'src/assets/static/scan_manager/ScanManagerErrorTable';
import Modal from 'src/components/Modal';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import useInvoiceS3UploadApi from 'src/services/useInvoiceS3UploadApi';
import useReceiptS3UploadApi from 'src/services/useReceiptS3UploadApi';
import useS3InvoiceAttach, { S3UploadInvoiceAttachRequest } from 'src/services/useS3InvoiceAttach';
import useS3ReceiptAttach from 'src/services/useS3ReceiptAttach';
import { SetState } from 'src/types/generic';
import { PreRequiredCheck, S3FileValidations, unProcessedFiles } from 'src/utils/validation';
import GridWrapper from './GridWrapper';
import { UploadPdfOnly } from './guards/PermissionsList';

export interface S3AttachmentResponse {
  processedFiles: string[];
  unProcessed: string[];
}

export interface S3UploadRes {
  fileName: string | null;
  signedUrl: string;
  fileNameWithAttachId: string;
  attachId: number;
}

const uploadRef = React.createRef<Upload>();
const hint = (
  <span>
    <i>Drop files here to upload</i>
  </span>
);
type UploadFilesModalProps = {
  closeModal: VoidFunction;
  open: boolean;
  companyId: number;
  refreshParent: VoidFunction;
  isInvoice: boolean;
  setRefreshCompany?: SetState<boolean>;
  id?: number;
};
const UploadFilesModal = ({
  closeModal,
  open,
  companyId,
  refreshParent,
  isInvoice,
  setRefreshCompany,
  id,
}: UploadFilesModalProps) => {
  const { S3Files: executeInvoiceUpload } = useInvoiceS3UploadApi();
  const { S3Files: executeReceiptUpload } = useReceiptS3UploadApi();
  const { S3InvoiceAttach } = useS3InvoiceAttach(companyId, id);
  const { S3ReceiptAttach } = useS3ReceiptAttach(companyId, id);
  const { confirm } = useConfirm();
  const isPDFOnly = usePermission({ permissions: UploadPdfOnly });
  const [overallProgress, setOverallProgress] = useState(0);

  const onProgress = useCallback((event: any) => {
    const totalProgress = event.affectedFiles.reduce(
      (acc: number, file: UploadFileInfo) => acc + (file.progress || 0),
      0
    );
    const averageProgress = totalProgress / event.affectedFiles.length;
    setOverallProgress(averageProgress);
  }, []);

  const onSelectFilesClick = () => {
    if (uploadRef) uploadRef?.current?.actionElement?.click();
  };

  const postAttachments = useCallback(
    async (
      attachMethod: Function,
      requestData: S3UploadRes[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;

      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      const modifiedReqData = requestData.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });
      const fileNames = modifiedReqData.map((file: any) => file.fileName);

      try {
        if (processed.length) {
          await attachMethod(modifiedReqData, id);
        }
        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    [id]
  );

  const putPresignedUrls = useCallback(
    async (
      s3response: S3UploadRes[],
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      awsResponse: string[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, awsResponse: [] };
      }
      const modifiedReqData = s3response.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });

      try {
        const awsResponse = await Promise.all(
          modifiedReqData.map(async (presignedUrls, index) => {
            // @ts-ignore
            const file = processedFiles[index].getRawFile();
            try {
              const response = await axios.put(presignedUrls.signedUrl, await file.arrayBuffer(), {
                headers: {
                  'Content-Type': file.type,
                },
              });
              const status = response.status === 200 ? 'success' : 'Fail';
              return status;
            } catch (error) {
              return 'Fail';
            }
          })
        );
        if (awsResponse.includes('Fail')) {
          preRequiredCheck.processed = [];
          const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
            fileName: fileName.name,
            errorMessage: 'Failed to upload file',
          }));
          preRequiredCheck.processed = [];
          preRequiredCheck.unProcessed.push(...errors);
          return { preRequiredCheck, awsResponse: ['Fail'] };
        }
        return { preRequiredCheck, awsResponse };
      } catch (error) {
        preRequiredCheck.processed = [];
        const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
          fileName: fileName.name,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, awsResponse: ['Fail'] };
      }
    },
    []
  );

  const getSignedUrls = useCallback(
    async (
      uploadMethod: Function,
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      s3response: S3UploadInvoiceAttachRequest[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, s3response: [] };
      }
      const fileNames = processedFiles.map((file: any) => file.name);
      try {
        const s3response: S3UploadInvoiceAttachRequest[] = await uploadMethod(fileNames);
        return { preRequiredCheck, s3response };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, s3response: [] };
      }
    },
    []
  );

  const onStatusChange = useCallback(
    async (e: UploadOnStatusChangeEvent) => {
      const { processedFiles, preRequiredCheck } = await S3FileValidations(
        e?.affectedFiles,
        isPDFOnly
      );
      // s3 file > s3 response, getting s3 url
      const { preRequiredCheck: getSignedUrlsPreReqCheck, s3response } = isInvoice
        ? await getSignedUrls(executeInvoiceUpload, processedFiles, preRequiredCheck)
        : await getSignedUrls(executeReceiptUpload, processedFiles, preRequiredCheck);
      //  s3 response > success or fail , uploading to s3 with file and url
      const { preRequiredCheck: putPresignedUrlsPreReqCheck } = await putPresignedUrls(
        s3response,
        processedFiles,
        getSignedUrlsPreReqCheck
      );

      // s3 response >  preRequiredCheck
      const { preRequiredCheck: postSignedUrlsPreReqCheck } = isInvoice
        ? await postAttachments(S3InvoiceAttach, s3response, putPresignedUrlsPreReqCheck)
        : await postAttachments(S3ReceiptAttach, s3response, putPresignedUrlsPreReqCheck);
      const { processed, unProcessed } = postSignedUrlsPreReqCheck;
      closeModal();
      confirm({
        title: 'File Upload',
        width: '650px',
        height: processed.length > 0 ? 'auto' : '380px',
        content: (
          <div className="font-bold ">
            <div className="font-bold ">
              {processed.length > 0 && (
                <>
                  {processed.length > 0 ? (
                    <>
                      <span className="font-extrabold text-lg m-2 text-green-600">
                        {processed.length}
                      </span>
                      file(s) uploaded successfully
                    </>
                  ) : (
                    ''
                  )}
                  {unProcessed?.length > 0 ? ' and' : ''}
                </>
              )}
              {unProcessed?.length > 0 && (
                <>
                  <span className="font-extrabold text-lg m-2 text-red-600">
                    {unProcessed?.length}
                  </span>
                  file(s) have failed
                  <GridWrapper
                    className="m-2 no_Veritcal_ScrollBar "
                    columns={ScanManagerErrorColumns}
                    dataSource={unProcessed}
                    DataItemKey={'fileName'}
                    pageable={false}
                    tableTextId="Errors"
                    disableAutoResize
                  />
                </>
              )}
            </div>
          </div>
        ),
        cancelButtonText: 'Close',
        cancelColor: 'info',
        hasNoConfirmButton: true,
      });
      setOverallProgress(0);
      refreshParent();
      setRefreshCompany?.((prev) => !prev);
    },
    [
      S3InvoiceAttach,
      S3ReceiptAttach,
      closeModal,
      confirm,
      executeInvoiceUpload,
      executeReceiptUpload,
      getSignedUrls,
      isInvoice,
      isPDFOnly,
      postAttachments,
      putPresignedUrls,
      refreshParent,
      setRefreshCompany,
    ]
  );

  const CustomListItemUI = ({ files }: { files: UploadFileInfo[] }) => (
    <ul className="list-none w-full h-[20vh]">
      {files?.map((file: UploadFileInfo, index: number) => (
        <li key={file.name} className={`flex flex-col ${index !== 0 ? 'mt-2' : ''}`}>
          <div className="flex items-center justify-between">
            <span>{file.name}</span>
            <span className="text-sm text-gray-500">{file.size} KB</span>
          </div>
          <div className="w-full bg-gray-200 h-1 rounded-full mt-1">
            <div
              className="bg-info h-full rounded-full"
              style={{ width: `${file.progress || 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
  const isUploadDisabled = useMemo(() => overallProgress >= 1, [overallProgress]);

  return (
    <div>
      <Modal
        title={'Upload Files'}
        closeModal={closeModal}
        visible={open}
        width="40vw"
        height="380px"
      >
        <div onClick={onSelectFilesClick}>
          <ExternalDropZone uploadRef={uploadRef} customHint={hint} disabled={isUploadDisabled} />
        </div>
        <Upload
          ref={uploadRef}
          disabled={isUploadDisabled}
          accept={isPDFOnly ? '.pdf' : '.pdf, image/jpg, image/jpeg, .gif, .bmp, image/png'}
          batch={true}
          multiple={true}
          defaultFiles={[]}
          withCredentials
          listItemUI={CustomListItemUI}
          onProgress={onProgress}
          className="k-dropzone-hint"
          onAdd={onStatusChange}
        />
        {overallProgress > 0 && <div>Overall Progress: {Math.round(overallProgress)}%</div>}
      </Modal>
    </div>
  );
};

export default UploadFilesModal;
